/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { FunctionComponent, useEffect, useMemo, useRef } from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { VodInteractor } from '../../core/interactors';
import { Locale, LocaleKeys } from '../../core/locale';
import { Log } from '../../core/logging';
import {
    KeyManager,
    LocationUrls,
    ScreenControllerTypes,
    ScreenTaskControllerTypes,
} from '../../core/navigation';
import { TScreen } from '../../core/navigation/types';
import { NetworkManager } from '../../core/network/NetworkManager';
import { useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { VodBrowseInfoCardUserInputHandler } from '../../core/usecases/VodBrowseInfoCardUserInputHandler';
import {
    vodBrowseMainScreenClearDataAction,
    vodBrowseMainScreenUpdateShouldHideAdultStateAction,
} from '../../store/vodbrowsemainscreen/vodbrowsemainscreen.actions';
import {
    onInfoCardDismissVodBrowseMainScreen,
    vodBrowseMainScreenClearData,
    vodBrowseMainScreenUpdateVerticalItems,
} from '../../store/vodbrowsemainscreen/vodbrowsemainscreen.reducers';
import { showWhisper } from '../../store/whisper/whisper.reducer';
import { ClockTypes } from '../Clock';
import { AntiburnOsd, BiaxialWidget, ScreenHeader } from '../common';
import { useInfoCard, useScreenController } from '../hooks';
import { Spinner } from '../VideoPlayerUI/Spinner';
import { Whisper } from '../Whisper';
import { WhisperTypes } from '../Whisper/WhisperTypes';

import './VodBrowseMainScreen.scss';

/* eslint-disable react-hooks/exhaustive-deps */
const VodBrowseMainScreen: FunctionComponent = (): JSX.Element => {
    const TAG = 'VodBrowseMainScreen';

    useScreenController<TScreen>({
        properties: {
            url: LocationUrls.VOD_BROWSE_MAIN_SCREEN,
            screenController: ScreenControllerTypes.VOD_BROWSE_MAIN_SCREEN,
            taskController: ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS,
        },
    });

    const { shouldHideAdultState, needsRefresh } = useTivoSelector(
        (state) => state.vodBrowseMainScreen,
    );
    const { isSpinnerActive } = useTivoSelector((state) => state.spinner);
    const { isActive: isAntiburnActive } = useTivoSelector((state) => state.antiburnOsd);

    const vodServices = useMemo(() => new VodInteractor(), []);
    const dispatch = useTivoDispatch();

    const onNetworkStateChangeCallback = () => {
        const isNetworkDisconnected = NetworkManager.isNetworkDisconnected();

        if (isNetworkDisconnected) {
            dispatch(
                showWhisper({
                    message: NetworkManager.getNetworkDisconnectedMessage(),
                    iconType: WhisperTypes.INFO,
                }),
            );
        }
    };

    useInfoCard(VodBrowseInfoCardUserInputHandler.name, () => {
        dispatch(onInfoCardDismissVodBrowseMainScreen());
    });

    useEffect(() => {
        Diagnostics.info(TAG, 'mounted.');
        Log.i(TAG, 'mounted.');
        const refNetworkManager = NetworkManager.subscribe(onNetworkStateChangeCallback);
        if (needsRefresh || shouldHideAdultState !== vodServices.shouldHideMetadata()) {
            dispatch(vodBrowseMainScreenClearDataAction());
        }
        dispatch(vodBrowseMainScreenUpdateVerticalItems());

        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }
        return () => {
            NetworkManager.unsubscribe(refNetworkManager);
            dispatch(
                vodBrowseMainScreenUpdateShouldHideAdultStateAction(
                    vodServices.shouldHideMetadata(),
                ),
            );
            Diagnostics.info(TAG, 'unmounted.');
            Log.i(TAG, 'unmounted.');
        };
    }, []);

    const isFirstAntiburnChangeRef = useRef(true);
    useEffect(() => {
        // skip effect on initial mount; only run when isAntiburnActive actually changes
        if (isFirstAntiburnChangeRef.current) {
            isFirstAntiburnChangeRef.current = false;
            return;
        }

        if (isAntiburnActive) {
            dispatch(vodBrowseMainScreenClearData());
        } else {
            dispatch(vodBrowseMainScreenUpdateVerticalItems());
        }
    }, [isAntiburnActive]);

    return (
        <div className="vod-browse-main-screen-container">
            <ScreenHeader
                title={Locale.getString(LocaleKeys.ON_DEMAND_TITLE)}
                clockType={ClockTypes.DEFAULT_CLOCK}
            />
            {!isAntiburnActive && <BiaxialWidget />}
            {isSpinnerActive && (
                <div className="vod-browse-loading-spinner">
                    <Spinner message="" />
                </div>
            )}
            <Whisper message="" />
            {isAntiburnActive && <AntiburnOsd />}
        </div>
    );
};

export default VodBrowseMainScreen;
